import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Camera, X, Maximize2, Minimize2, Video } from 'lucide-react';

// Sửa lỗi icon mặc định của Leaflet không hiển thị trong môi trường React
import iconUrl from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

const DefaultIcon = L.icon({
    iconUrl,
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
});

L.Marker.prototype.options.icon = DefaultIcon;

/**
 * Tạo icon xe tùy chỉnh dựa trên trạng thái của thiết bị
 * @param {string} status - Trạng thái (running, stopped, lost, no_gps, unlocked)
 */
const createVehicleIcon = (status) => {
    const colors = {
        running: '#22c55e',  // Xanh lá - Đang chạy
        stopped: '#ef4444',  // Đỏ - Dừng
        lost: '#9ca3af',     // Xám - Mất kết nối
        no_gps: '#3b82f6',   // Xanh dương - Dừng GPS
        unlocked: '#eab308', // Vàng - Dừng mở khóa
    };
    const fillColor = colors[status] || '#9ca3af';

    // SVG cho icon xe chuyên nghiệp
    const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 36 36">
      <circle cx="18" cy="18" r="16" fill="${fillColor}" stroke="white" stroke-width="2.5" opacity="0.95"/>
      <g transform="translate(8, 8)" fill="white">
        <rect x="1" y="5" width="18" height="10" rx="2" ry="2"/>
        <rect x="3" y="3" width="14" height="7" rx="2" ry="2" opacity="0.8"/>
        <circle cx="5" cy="16" r="2.5"/>
        <circle cx="15" cy="16" r="2.5"/>
        <rect x="0" y="9" width="2" height="4" rx="1" opacity="0.7"/>
        <rect x="18" y="9" width="2" height="4" rx="1" opacity="0.7"/>
      </g>
    </svg>
  `;

    return L.divIcon({
        html: svg,
        className: '',
        iconSize: [36, 36],
        iconAnchor: [18, 18],
        popupAnchor: [0, -18],
    });
};

/**
 * Component hỗ trợ cập nhật tâm bản đồ khi thiết bị được chọn thay đổi
 */
const MapUpdater = ({ center }) => {
    const map = useMap();
    useEffect(() => {
        if (center) {
            // Di chuyển bản đồ đến vị trí mới với hiệu ứng mượt
            map.setView(center, map.getZoom(), { animate: true });
        }
    }, [center, map]);
    return null;
};

// Các mục chú thích trạng thái trên bản đồ
const LEGEND_ITEMS = [
    { color: 'bg-green-500', label: 'Đang chạy' },
    { color: 'bg-red-500', label: 'Dừng' },
    { color: 'bg-yellow-400', label: 'Dừng mở khóa' },
    { color: 'bg-blue-500', label: 'Dừng GPS' },
    { color: 'bg-gray-500', label: 'Mất kết nối' },
    { color: 'bg-purple-500', label: 'Truyền lịch sử' },
    { color: 'bg-orange-500', label: 'Hết hạn đi' },
];

/**
 * Xác định trạng thái của thiết bị dựa trên dữ liệu GPS
 */
const getDeviceStatus = (device) => {
    if (!device) return 'lost';
    const now = Math.floor(Date.now() / 1000);
    const diff = now - (device.gpsdate_unix || 0);

    // Nếu dữ liệu cũ hơn 5 phút (300 giây) thì coi như mất kết nối
    if (diff > 300) return 'lost';

    // Kiểm tra trạng thái máy (ACC) và tốc độ
    if (device.acc_status === 'On' && device.speed_kmh > 0) return 'running';
    if (device.acc_status === 'On') return 'stopped';

    return 'no_gps';
};

const formatTime = (unix) => {
    if (!unix) return '--';
    return new Date(unix * 1000).toLocaleString('vi-VN');
};

/**
 * Component hiển thị khung Camera Live
 */
const CameraPanel = ({ id, label, isOpen, onToggle }) => {
    const [expanded, setExpanded] = useState(false);

    return (
        <div
            style={{
                width: !isOpen ? '120px' : expanded ? '480px' : '240px',
                height: !isOpen ? '36px' : expanded ? '270px' : '170px',
                transition: 'width 0.3s ease, height 0.3s ease',
            }}
            className="flex flex-col bg-gray-900 rounded-xl overflow-hidden shadow-2xl border border-gray-700"
        >
            {/* Header của Camera - luôn hiển thị */}
            <div
                className="flex items-center justify-between px-2 py-1.5 bg-gray-800 cursor-pointer flex-shrink-0"
                onClick={() => onToggle(id)}
            >
                <div className="flex items-center gap-1.5 min-w-0">
                    <div className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse flex-shrink-0"></div>
                    <Camera size={13} className="text-orange-400 flex-shrink-0" />
                    <span className="text-[11px] font-bold text-white truncate">{label}</span>
                </div>
                <div className="flex items-center gap-1 ml-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                    {isOpen && (
                        <button
                            onClick={() => setExpanded((p) => !p)}
                            className="text-gray-400 hover:text-white p-0.5 transition-colors"
                            title={expanded ? 'Thu nhỏ' : 'Phóng to'}
                        >
                            {expanded ? <Minimize2 size={12} /> : <Maximize2 size={12} />}
                        </button>
                    )}
                    <button
                        onClick={() => onToggle(id)}
                        className="text-gray-400 hover:text-white p-0.5 transition-colors"
                        title={isOpen ? 'Ẩn camera' : 'Mở camera'}
                    >
                        {isOpen ? <X size={12} /> : <Camera size={12} />}
                    </button>
                </div>
            </div>

            {/* Vùng hiển thị video camera */}
            {isOpen && (
                <div className="flex-1 relative bg-black flex items-center justify-center overflow-hidden">
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
                        <Video size={32} className="text-gray-600" />
                        <span className="text-[10px] text-gray-500 font-medium">Không có tín hiệu</span>
                        <span className="text-[9px] text-gray-600">{label}</span>
                    </div>

                    {/* Nhãn LIVE */}
                    <div className="absolute top-2 left-2 flex items-center gap-1 bg-red-600/90 rounded px-1.5 py-0.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
                        <span className="text-[9px] text-white font-black uppercase tracking-wider">Live</span>
                    </div>

                    {/* Thời gian hiện tại */}
                    <div className="absolute bottom-2 right-2 text-[9px] text-gray-500 font-mono bg-black/60 px-1.5 py-0.5 rounded">
                        {new Date().toLocaleTimeString('vi-VN')}
                    </div>
                </div>
            )}
        </div>
    );
};

const MapComponent = ({ devices = [], selectedDevice, onSelectDevice }) => {
    // Tính toán tâm bản đồ (Vị trí thiết bị được chọn hoặc mặc định là Hà Nội)
    const center = selectedDevice
        ? [selectedDevice.latitude, selectedDevice.longitude]
        : devices.length > 0
            ? [devices[0].latitude, devices[0].longitude]
            : [21.0278, 105.8542];

    const [camerasOpen, setCamerasOpen] = useState(false); // Trạng thái hiển thị cả 2 camera cùng lúc

    return (
        <div className="flex-1 relative overflow-hidden">

            {/* Chú thích trạng thái nằm đè lên bản đồ (Legend Overlay) */}
            <div className="absolute top-0 left-0 right-0 z-[1000] bg-white/90 backdrop-blur-sm border-b border-gray-200 px-3 py-1.5 flex items-center gap-1 flex-wrap">
                <button className="p-1 hover:bg-gray-100 rounded text-gray-600 mr-1">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                </button>
                {LEGEND_ITEMS.map((item) => (
                    <div key={item.label} className="flex items-center gap-1 mr-2">
                        <span className={`inline-block w-2.5 h-2.5 rounded-full ${item.color}`}></span>
                        <span className="text-[10px] text-gray-600 font-medium whitespace-nowrap">{item.label}</span>
                    </div>
                ))}
            </div>

            {/* Bản Đồ Leaflet */}
            <MapContainer
                center={center}
                zoom={13}
                scrollWheelZoom={true}
                style={{ height: '100%', width: '100%' }}
                zoomControl={false}
            >
                <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />

                {/* Tự động cập nhật vị trí bản đồ */}
                <MapUpdater center={center} />

                {/* Hiển thị Marker cho từng thiết bị */}
                {devices.map((device, index) => {
                    if (!device.latitude || !device.longitude) return null;
                    const status = getDeviceStatus(device);
                    const icon = createVehicleIcon(status);

                    return (
                        <Marker
                            key={device.imei || index}
                            position={[device.latitude, device.longitude]}
                            icon={icon}
                            eventHandlers={{
                                click: () => onSelectDevice && onSelectDevice(device),
                            }}
                        >
                            <Popup maxWidth={260}>
                                <div className="font-sans p-1 min-w-[220px]">
                                    <div className="font-bold text-gray-800 text-sm border-b pb-1 mb-2 flex items-center gap-2">
                                        <span className="text-lg">🚗</span>
                                        {device.imei}
                                    </div>
                                    <div className="space-y-1 text-xs">
                                        <div className="flex justify-between">
                                            <span className="text-gray-500">Tốc độ</span>
                                            <span className="font-semibold">{device.speed_kmh} km/h</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-500">Tọa độ</span>
                                            <span className="font-semibold">{device.latitude.toFixed(5)}, {device.longitude.toFixed(5)}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-500">Cập nhật</span>
                                            <span className="font-semibold">{formatTime(device.gpsdate_unix)}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-500">Trạng thái</span>
                                            <span className={`font-bold ${device.acc_status === 'On' ? 'text-green-600' : 'text-red-600'}`}>
                                                {device.acc_status === 'On' ? 'Đang bật' : 'Đã tắt'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </Popup>
                        </Marker>
                    );
                })}
            </MapContainer>

            {/* Các bảng điều khiển camera nổi trên bản đồ */}
            {camerasOpen && (
                <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-[1000] flex items-center gap-3 pointer-events-none">
                    <div className="pointer-events-auto">
                        <CameraPanel
                            id="cam1"
                            label="Camera Trước"
                            isOpen={true}
                            onToggle={() => setCamerasOpen(false)}
                        />
                    </div>
                    <div className="pointer-events-auto">
                        <CameraPanel
                            id="cam2"
                            label="Camera Sau"
                            isOpen={true}
                            onToggle={() => setCamerasOpen(false)}
                        />
                    </div>
                </div>
            )}

            {/* Thanh công cụ nhanh để bật/tắt cả 2 camera */}
            <div className="absolute bottom-1 left-1/2 -translate-x-1/2 z-[1000] flex items-center gap-2 bg-gray-900/90 backdrop-blur-sm rounded-full px-4 py-2 shadow-2xl pointer-events-auto transition-all hover:bg-gray-900 border border-gray-700">
                <Video size={14} className="text-red-500 animate-pulse" />
                <span className="text-[11px] text-gray-300 font-medium whitespace-nowrap mr-1">Giám sát xe</span>
                <button
                    onClick={() => setCamerasOpen(!camerasOpen)}
                    className={`${camerasOpen ? 'bg-red-600' : 'bg-gray-700'} hover:bg-red-500 text-white text-[10px] font-bold px-4 py-1.5 rounded-full transition-all flex items-center gap-2`}
                >
                    <span className="w-2 h-2 rounded-full bg-white animate-pulse"></span>
                    {camerasOpen ? 'Tắt Camera (2)' : 'Bật Camera (2)'}
                </button>
            </div>
        </div>
    );
};

export default MapComponent;